import React, { useState } from 'react';
import { clsx } from 'clsx';
import { useLanguage } from '../../../features/language/useLanguage';

interface RoastAgtronSectionProps {
  showTargetLabels: boolean;
  agtronWhole: string;
  agtronGround: string;
  agtronWholeResult: { common: string; scaa: string };
  agtronGroundResult: { common: string; scaa: string };
  onAgtronChange: (field: 'whole' | 'ground', value: string) => void;
  roastType: string;
  onRoastTypeChange: (value: string) => void;
  readOnly: boolean;
  readOnlyRoastType?: boolean;
  allowRoastTypeEdit?: boolean;
}

export const RoastAgtronSection: React.FC<RoastAgtronSectionProps> = ({
  showTargetLabels,
  agtronWhole,
  agtronGround,
  agtronWholeResult,
  agtronGroundResult,
  onAgtronChange,
  roastType,
  onRoastTypeChange,
  readOnly,
  readOnlyRoastType = false,
  allowRoastTypeEdit = false,
}) => {
  const { t } = useLanguage();
  const [agtronType, setAgtronType] = useState<'common' | 'scaa'>('scaa');

  const diff = Number(agtronWhole) - Number(agtronGround);
  const normalizeAgtronInput = (nextValue: string) => {
    const sanitized = nextValue.replace(',', '.');
    if (sanitized === '') {
      return '';
    }
    if (!/^\d*\.?\d*$/.test(sanitized)) {
      return '';
    }
    if (sanitized === '.') {
      return '0.';
    }
    let normalized = sanitized;
    if (normalized.length > 1 && normalized[0] === '0' && normalized[1] !== '.') {
      const trimmed = normalized.replace(/^0+/, '');
      normalized = trimmed === '' ? '0' : trimmed;
    }
    const [integerPart, decimalPart] = normalized.split('.');
    if (decimalPart !== undefined && decimalPart.length > 2) {
      return `${integerPart}.${decimalPart.slice(0, 2)}`;
    }
    return normalized;
  };

  const clampAgtronInput = (nextValue: string) => {
    const normalized = normalizeAgtronInput(nextValue);
    if (normalized === '' || normalized === '0.') {
      return normalized;
    }

    const numericValue = Number(normalized);
    if (!Number.isFinite(numericValue)) {
      return '';
    }

    const clamped = Math.min(100, Math.max(0, numericValue));
    if (clamped !== numericValue) {
      return String(clamped);
    }
    return normalized;
  };

  return (
    <div className="bg-white border rounded-lg p-4 space-y-4">
      <div className="flex justify-between items-center">
        <h4 className="text-sm font-medium text-gray-700">
          {showTargetLabels ? t('roast.agtron.targetAgtronMeter') : t('roast.agtron.agtronMeter')}
        </h4>

        <div className="flex items-center bg-gray-100 rounded-lg p-1">
          <button
            type="button"
            onClick={() => setAgtronType('scaa')}
            className={clsx(
              'px-2 py-1 text-xs font-bold rounded-md transition-all',
              agtronType === 'scaa' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
            )}
          >
            {t('roast.agtron.scaa')}
          </button>
          <button
            type="button"
            onClick={() => setAgtronType('common')}
            className={clsx(
              'px-2 py-1 text-xs font-bold rounded-md transition-all',
              agtronType === 'common' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
            )}
          >
            {t('roast.agtron.common')}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-xs text-gray-500 mb-1">{t('roast.agtron.wholeBean')}</label>
          <input
            type="text"
            inputMode="decimal"
            value={agtronWhole}
            placeholder={t('roast.agtron.zeroPlaceholder')}
            disabled={readOnly}
            onChange={event => onAgtronChange('whole', clampAgtronInput(event.target.value))}
            className="block w-full text-sm border-gray-300 rounded-md border p-1.5"
          />
          <div className="mt-2 text-xs text-blue-900 tracking-widest font-medium uppercase text-center bg-blue-300 py-2 rounded-lg">
            {agtronType === 'common' ? agtronWholeResult.common : agtronWholeResult.scaa}
          </div>
        </div>

        <div>
          <label className="block text-xs text-gray-500 mb-1">{t('roast.agtron.groundBean')}</label>
          <input
            type="text"
            inputMode="decimal"
            value={agtronGround}
            placeholder={t('roast.agtron.zeroPlaceholder')}
            disabled={readOnly}
            onChange={event => onAgtronChange('ground', clampAgtronInput(event.target.value))}
            className="block w-full text-sm border-gray-300 rounded-md border p-1.5"
          />
          <div className="mt-2 text-xs text-blue-900 tracking-widest font-medium uppercase text-center bg-blue-300 py-2 rounded-lg">
            {agtronType === 'common' ? agtronGroundResult.common : agtronGroundResult.scaa}
          </div>
        </div>
      </div>

      <div className="flex justify-between items-end mt-4">
        <div className="w-1/2">
          <label className="block text-xs text-gray-600 mb-1">{t('workOrders.table.roastType')}</label>
          <select
            value={roastType}
            disabled={(readOnly || readOnlyRoastType) && !allowRoastTypeEdit}
            onChange={event => onRoastTypeChange(event.target.value)}
            className="block w-full border-gray-300 rounded-md border p-1.5 text-sm"
          >
            <option value="">{t('workOrders.beanSelection.selectRoastType')}</option>
            <option value="sample">{t('workOrders.beanSelection.roast.sample')}</option>
            <option value="filter">{t('workOrders.beanSelection.roast.filter')}</option>
            <option value="omni">{t('workOrders.beanSelection.roast.omni')}</option>
            <option value="espresso">{t('workOrders.beanSelection.roast.espresso')}</option>
          </select>
        </div>

        <div className="flex flex-col items-start">
          <label className="text-xs text-gray-600 mb-1">{t('roast.agtron.difference')}</label>

          {agtronWhole !== '' &&
          agtronGround !== '' &&
          Number.isFinite(Number(agtronWhole)) &&
          Number.isFinite(Number(agtronGround)) ? (
            <div className="min-w-[100px] px-4 py-2 text-xs font-semibold rounded-md bg-gray-200 text-center">
              {diff.toFixed(2)}
            </div>
          ) : (
            <div className="w-[100px] h-[34px] bg-gray-50 rounded-md border border-dashed border-gray-200"></div>
          )}
        </div>
      </div>
    </div>
  );
};
